import {
  MicrophoneDenialReason,
  ScribeRecordingPhase,
  ScribeSessionStatus,
  ScribeStatus,
} from '@principle-theorem/principle-core/interfaces';

/**
 * Pure derivations of the presentation-facing scribe enums from the raw
 * lifecycle inputs the session service holds. Extracted from
 * `AiScribeSessionService` so this mapping is directly unit-testable and the
 * service is left holding only the stateful DI, signal and lifecycle wiring: the
 * service feeds in its current capture phase, persisted session status and mic
 * errors, and renders whatever these return.
 */
export class ScribeStatusResolver {
  /**
   * The status a consumer renders, derived from the client-side capture phase
   * and the persisted session status. The microphone handshake precedes any
   * session doc, so those phases resolve before the persisted status; the
   * post-stop writing window is likewise a phase the persisted `in-progress`
   * status cannot express. `sessionStatus` is `undefined` exactly when no
   * session is active yet.
   */
  static rendered(
    phase: ScribeRecordingPhase,
    sessionStatus: ScribeSessionStatus | undefined
  ): ScribeStatus {
    if (phase === ScribeRecordingPhase.RequestingPermission) {
      return ScribeStatus.RequestingPermission;
    }
    if (phase === ScribeRecordingPhase.PermissionDenied) {
      return ScribeStatus.PermissionDenied;
    }
    if (sessionStatus === undefined) {
      return ScribeStatus.Idle;
    }
    if (sessionStatus === ScribeSessionStatus.Ready) {
      return ScribeStatus.Ready;
    }
    if (sessionStatus === ScribeSessionStatus.Failed) {
      return ScribeStatus.Failed;
    }
    if (phase === ScribeRecordingPhase.Connecting) {
      return ScribeStatus.Connecting;
    }
    return phase === ScribeRecordingPhase.Writing
      ? ScribeStatus.Writing
      : ScribeStatus.InProgress;
  }

  /**
   * Classify a `getUserMedia` rejection into the reason a consumer varies its
   * recovery copy on: a missing capture device (`NotFoundError`) reads as
   * `Unavailable`, every other rejection as a blocked prompt (`Denied`).
   */
  static microphoneDenialReason(error: unknown): MicrophoneDenialReason {
    if (error instanceof DOMException && error.name === 'NotFoundError') {
      return MicrophoneDenialReason.Unavailable;
    }
    return MicrophoneDenialReason.Denied;
  }
}
